import { useTheme } from '@retailer-platform/shared-components/src/tds'
import { CartIcon } from '@instacart/ids-core'
import { tdsNavIcon } from './tdsNavIcon'
import { cloneDeep, sortBy } from 'lodash-es'
import { useMemo } from 'react'
import { useStoreConfigurationsByPartnerId } from '../../../api/store-configurations/useStoreConfigurations.hooks'
import { StoreConfigurationRelationshipEnum } from '../../../__codegen__/api'
import { useOptionalPartnerContext } from '../../../utils/contexts/partner/PartnerContext.hooks'
import { PartnerType } from '../../../legacy/common/types'
import usePartnerAccessControl from '../../../legacy/components/AccessControl/usePartnerAccessControl'
import { type RPPNavigationEntry } from '../../../utils/core/RPPCore.types'
import { useCoreContext } from '../../../utils/core/RPPCoreContext'
import { getEligibleSiteTier, type SiteTier } from '../../../utils/store-config/storeConfigSiteTier'
import { isStoreConfigVisibleToPartner } from '../../../utils/store-config/storeConfigVisibility'
import { AlcoholTlogsAccessControl } from '../../../legacy/routes/reports/alcohol/AlcoholTlogs.configuration'
import { CatalogSectionAccessControl } from '../../../legacy/sections/catalog/CatalogSection.configuration'
import { InventoryFilesAccessControl } from '../../../legacy/routes/reports/files/InventoryFiles.configuration'
import { legacyDepartmentsAccessControl } from '../../../legacy/routes/catalog/departments/retailerCollectionsV2Access.configuration'
import { StoreGroupsSectionNavigationControl } from '../../../legacy/sections/store-groups/StoreGroupsSection.configuration'
import {
  type NavMenuHierarchyL1,
  type NavMenuHierarchyL2,
  clearEmptyBranches,
  indexRoutes,
  removeInsufficientAccessNavEntries,
} from '../nav-v2/NavMenuHierarchy'
import { NavEntryPointV3 } from './navEntryPointsV3'
import { NAV_SECTION_TITLES, NavSectionV3, type NavMenuHierarchyV3 } from './navSectionV3'

// v3 sidebar IA — canonical "Nav Pattern Overview" Figma (node 8504:30873).
// 12 L1s grouped under four section headers (Global, Commerce, Operations,
// Developer). Sections are first-class structure: the static hook returns a
// list of { id, l1s } sections, the wrapping hook iterates sections, and the
// renderer renders sections directly.
//
// Each non-Home L1 attaches domain entries directly as leaves via
// `navEntryPointV3`. No inline subsection L2s — the IA flattens v2's
// Analytics-with-subsections shape into Performance + Reports as siblings.
// `hasL2Children` remains unset because there are no intermediate subsections;
// `TdsNavigationBar` renders each attached child as a leaf `NavItem`.
//
// Icons use the TDS NavigationBar icon set (`NavigationBar.Icon name=...`),
// which ships purpose-built glyphs that match the Figma sidebar spec. The
// `caper` L1 falls back to `@instacart/ids-core` `CartIcon` because TDS does
// not expose a caper glyph yet.
const useNavHierarchyRetailerDataV3Static = (): NavMenuHierarchyV3 => {
  const theme = useTheme()
  const { partnerType } = useOptionalPartnerContext() || {}

  return useMemo(() => {
    const home: NavMenuHierarchyL1 = {
      name: 'navV3.folder.home.title',
      icon: tdsNavIcon('home'),
      // Home is a non-expandable L1: clicking it navigates directly instead of
      // toggling a NavGroupContent panel. The route mirrors `homeRoute` below.
      route:
        partnerType === PartnerType.IDP ? 'instacart-developer-platform-get-started' : 'dashboard',
      children: [],
    }
    const performance: NavMenuHierarchyL1 = {
      name: 'navV3.folder.performance.title',
      icon: tdsNavIcon('analytics'),
      navEntryPointV3: NavEntryPointV3.Performance,
      children: [],
    }
    const reports: NavMenuHierarchyL1 = {
      name: 'navV3.folder.reports.title',
      icon: tdsNavIcon('reports'),
      navEntryPointV3: NavEntryPointV3.Reports,
      // Alcohol delivery logs lives in the legacy dashboard package, not a
      // domain, so it can't register via `attachToV3`. Add it as a static
      // child — `attachDomainsV3` merges and re-sorts by `positionNavV3`.
      children: [
        {
          name: 'alcoholTlogs.title',
          route: 'alcohol-tlogs',
          accessControl: AlcoholTlogsAccessControl,
          positionNavV3: 3,
        },
      ],
    }
    const catalog: NavMenuHierarchyL1 = {
      name: 'navV2.folder.catalog.title',
      icon: tdsNavIcon('catalog'),
      navEntryPointV3: NavEntryPointV3.Catalog,
      // New product and Inventory files live in the legacy dashboard package,
      // not domains, so they can't register via `attachToV3`. Add them as
      // static children — `attachDomainsV3` merges and re-sorts by
      // `positionNavV3`. Inventory files renders a divider above it per the
      // Figma cluster (separates from the configurable products group).
      children: [
        {
          name: 'catalog.newProduct.v3.title',
          route: 'new-product-request',
          accessControl: CatalogSectionAccessControl,
          positionNavV3: 4,
        },
        {
          name: 'reports.files.title',
          route: 'files',
          accessControl: InventoryFilesAccessControl,
          positionNavV3: 6,
          dividerBefore: true,
        },
      ],
    }
    const marketplace: NavMenuHierarchyL1 = {
      name: 'navV3.folder.marketplace.title',
      icon: tdsNavIcon('marketplace'),
      navEntryPointV3: NavEntryPointV3.Marketplace,
      children: [],
    }
    const storefront: NavMenuHierarchyL1 = {
      name: 'navV3.folder.storefront.title',
      // Storefront and Storefront Pro share the same L1 slot (mutually
      // exclusive — see `TIER_L1_VISIBLE` below). They render the same icon
      // per Figma so the slot looks identical regardless of which tier the
      // user has access to.
      icon: tdsNavIcon('storefrontPro'),
      navEntryPointV3: NavEntryPointV3.Storefront,
      children: [],
    }
    const storefrontPro: NavMenuHierarchyL1 = {
      name: 'navV3.folder.storefrontPro.title',
      icon: tdsNavIcon('storefrontPro'),
      navEntryPointV3: NavEntryPointV3.StorefrontPro,
      // Legacy Departments lives in the legacy dashboard package, not a domain,
      // so it can't register via `attachToV3`. Add as a static child between
      // Departments (12) and Cart assistant (13) per the Figma cluster.
      children: [
        {
          name: 'catalog.department.legacy',
          route: 'departments',
          accessControl: legacyDepartmentsAccessControl,
          positionNavV3: 12.5,
        },
      ],
    }
    const customersAndOrders: NavMenuHierarchyL1 = {
      name: 'navV3.folder.customersAndOrders.title',
      icon: tdsNavIcon('orders'),
      navEntryPointV3: NavEntryPointV3.CustomersAndOrders,
      children: [],
    }
    const stores: NavMenuHierarchyL1 = {
      name: 'navV3.folder.stores.title',
      icon: tdsNavIcon('stores'),
      navEntryPointV3: NavEntryPointV3.Stores,
      // Store groups lives in the legacy dashboard package, not a domain, so
      // it can't register via `attachToV3`. Add as a static child at position
      // 0 so it surfaces as the first entry per the Figma cluster.
      children: [
        {
          name: 'settings.storeGroups.title',
          route: 'store-groups',
          accessControl: StoreGroupsSectionNavigationControl,
          positionNavV3: 0,
        },
      ],
    }
    const fulfillment: NavMenuHierarchyL1 = {
      name: 'navV3.folder.fulfillment.title',
      icon: tdsNavIcon('fulfillment'),
      navEntryPointV3: NavEntryPointV3.Fulfillment,
      children: [],
    }
    const caper: NavMenuHierarchyL1 = {
      // CartIcon is from ids-core, not the TDS NavigationBar icon set, so it
      // stays as a direct-render icon (no outline/filled variants today).
      name: 'navV3.folder.caper.title',
      icon: CartIcon,
      navEntryPointV3: NavEntryPointV3.Caper,
      children: [],
    }
    const developer: NavMenuHierarchyL1 = {
      name: 'navV2.folder.developer.title',
      icon: tdsNavIcon('developer'),
      navEntryPointV3: NavEntryPointV3.Developer,
      children: [],
    }

    return {
      navBarColor: theme.colors.brandPrimaryDark,
      navBarColorHover: theme.colors.brandPrimaryRegular,
      type: 'retailer',
      // Home L1 navigates here; matches v2 behaviour for IDP vs. Retailer landing routes.
      homeRoute:
        partnerType === PartnerType.IDP ? 'instacart-developer-platform-get-started' : 'dashboard',
      sections: [
        // `Global` deliberately omits a titleId so the renderer suppresses its
        // section header — the top group reads as "Home + Performance + Reports"
        // without a label, matching the Figma. Other sections map their id to
        // the canonical NAV_SECTION_TITLES entry.
        { id: NavSectionV3.Global, l1s: [home, performance, reports] },
        {
          id: NavSectionV3.Commerce,
          titleId: NAV_SECTION_TITLES[NavSectionV3.Commerce],
          l1s: [catalog, marketplace, storefront, storefrontPro],
        },
        {
          id: NavSectionV3.Operations,
          titleId: NAV_SECTION_TITLES[NavSectionV3.Operations],
          l1s: [customersAndOrders, stores, fulfillment, caper],
        },
        {
          id: NavSectionV3.Developer,
          titleId: NAV_SECTION_TITLES[NavSectionV3.Developer],
          l1s: [developer],
        },
      ],
    } satisfies NavMenuHierarchyV3
  }, [theme.colors.brandPrimaryDark, theme.colors.brandPrimaryRegular, partnerType])
}

// Mirrors v2's `attachDomains` shape but reads `navEntryPointV3` from L1/L2 nodes
// and looks up entries in a NavEntryPointV3-keyed map.
const attachDomainsV3 = (
  currentNode: NavMenuHierarchyL1 | NavMenuHierarchyL2,
  entriesByAttachToV3: Partial<Record<NavEntryPointV3, RPPNavigationEntry[]>>
) => {
  currentNode.children?.forEach(child => {
    attachDomainsV3(child, entriesByAttachToV3)
  })

  if (currentNode.navEntryPointV3) {
    const ownEntries = entriesByAttachToV3[currentNode.navEntryPointV3 as NavEntryPointV3] ?? []
    const newChildren: NavMenuHierarchyL2[] = ownEntries.map(e => ({
      ...e,
      name: e.labelIdV3 ?? e.labelId,
    }))
    currentNode.children = sortBy(
      (currentNode.children || []).concat(newChildren),
      child => child.positionNavV3 ?? 0
    )
  }
}

export const useNavHierarchyRetailerV3 = (doAccessCheck: boolean = true) => {
  const hasAccess = usePartnerAccessControl()
  const { domainNavigationEntriesV3 = {} } = useCoreContext()
  const navMenuHierarchy = useNavHierarchyRetailerDataV3Static()

  const partnerCtx = useOptionalPartnerContext()
  const partnerId = partnerCtx?.partnerId
  const { data: storeConfigsData } = useStoreConfigurationsByPartnerId(
    {
      partnerId: partnerId ?? '',
      storeConfigRelationship: StoreConfigurationRelationshipEnum.Associated,
    },
    { skip: !partnerId }
  )

  // While the query is loading or skipped (no partnerId), this set is empty,
  // so all tier-gated L1s stay hidden until we know which tiers the user can
  // access. Intentionally avoids flashing tier L1s the user may not have.
  const accessibleTiers = useMemo<Set<SiteTier>>(() => {
    const tiers = new Set<SiteTier>()
    storeConfigsData?.storeConfigurationsByPartnerId?.forEach(sc => {
      if (!sc) return
      // The query returns Caper-only and other partner-not-visible configs that
      // the user never sees in the scope picker. Skip those — they would
      // otherwise inflate the tier set (every non-marketplace UIC classifies as
      // 'storefrontPro') and surface a phantom SFP L1 for retailers who have
      // no real SFP access.
      if (!isStoreConfigVisibleToPartner(sc)) return
      const tier = getEligibleSiteTier({
        storeConfigurationId: sc.id,
        userIsolationContextId: sc.userIsolationContextId,
        allowAllWarehouses: sc.allowAllWarehouses,
      })
      if (tier === null) return
      tiers.add(tier)
    })
    return tiers
  }, [storeConfigsData])

  return useMemo(() => {
    const indexedL1ByRoutes: { [key: string]: NavMenuHierarchyL1 } = {}
    const clonedHierarchy = cloneDeep(navMenuHierarchy)

    const showMarketplace = accessibleTiers.has('marketplace')
    const showStorefrontPro = accessibleTiers.has('storefrontPro')
    const showStorefront = accessibleTiers.has('storefront')

    const TIER_L1_VISIBLE: Partial<Record<NavEntryPointV3, boolean>> = {
      [NavEntryPointV3.Marketplace]: showMarketplace,
      [NavEntryPointV3.Storefront]: showStorefront,
      [NavEntryPointV3.StorefrontPro]: showStorefrontPro,
    }

    clonedHierarchy.sections.forEach(section => {
      // Drop tier-gated L1s the user has no matching store config for, before
      // the leaf-or-route filter below — a tier-gated L1 with kids it can't
      // surface should still get culled by tier first.
      section.l1s = section.l1s.filter(l1 => {
        if (!l1.navEntryPointV3) return true
        const decided = TIER_L1_VISIBLE[l1.navEntryPointV3 as NavEntryPointV3]
        return decided === undefined ? true : decided
      })

      section.l1s.forEach(l1 => {
        attachDomainsV3(l1, domainNavigationEntriesV3)
        if (doAccessCheck) {
          removeInsufficientAccessNavEntries(l1, hasAccess)
        }
        clearEmptyBranches(l1)
        indexRoutes(l1, l1, indexedL1ByRoutes)
      })
      // Keep L1s that either have surviving children or are leaf L1s (own
      // route/href) — leaf L1s legitimately have no children.
      section.l1s = section.l1s.filter(
        l1 => l1.children.length > 0 || Boolean(l1.route) || Boolean(l1.href)
      )
    })

    clonedHierarchy.sections = clonedHierarchy.sections.filter(s => s.l1s.length > 0)

    return { navMenuHierarchy: clonedHierarchy, indexedL1ByRoutes }
  }, [navMenuHierarchy, domainNavigationEntriesV3, hasAccess, doAccessCheck, accessibleTiers])
}
